/** @format */

import { useContext, useMemo } from 'react';

import { EventContext } from '../../Contexts/EventContext';

const ADMIN_ROLES = ['admin', 'Super Admin', 'Developer'];
const FEDERATION_ROLES = ['federation', 'Federation', 'zone'];
const FINANCIAL_ROLES = ['financial', 'Financial'];
const BOARD_PREFIX = 'board_';

const hasAnyRole = (roleNames, needles) =>
  needles.some((role) => roleNames.includes(role));

const isBoardRole = (role) =>
  role.startsWith(BOARD_PREFIX) ||
  role.includes('هیئت') ||
  role.startsWith('Board ') ||
  role === 'province board';

export const useAccess = () => {
  const { authRoles = [] } = useContext(EventContext);

  const roleNames = useMemo(
    () => authRoles.map((role) => role?.name).filter(Boolean),
    [authRoles],
  );

  const isAdmin = () => hasAnyRole(roleNames, ADMIN_ROLES);
  const isFederation = () => isAdmin() || hasAnyRole(roleNames, FEDERATION_ROLES);
  const isFinancial = () => isAdmin() || hasAnyRole(roleNames, FINANCIAL_ROLES);
  const isProvinceBoard = () =>
    roleNames.some(isBoardRole) || roleNames.includes('zone');

  const provinceBoardId = () => {
    const boardRole = roleNames.find((role) => role.startsWith(BOARD_PREFIX));

    if (boardRole) {
      return Number(boardRole.replace(BOARD_PREFIX, '')) || null;
    }

    const legacyBoard = roleNames.find((role) => role.startsWith('Board '));

    if (legacyBoard) {
      const match = legacyBoard.match(/Board\s+(\d+)/);
      return match ? Number(match[1]) : null;
    }

    return null;
  };

  const canListClubs = () => isFederation() || isProvinceBoard();
  const canReviewClubs = () => isFederation() || isProvinceBoard();
  const canManageUsers = () => isAdmin();
  const canManageRoles = () => isAdmin();
  const canManagePaymentSettings = () => isAdmin();
  const canManageMatches = () => isFederation() || isProvinceBoard();
  const canReadBaseData = () => isFederation() || isProvinceBoard();
  const canManageBaseData = () => isFederation();
  const canViewFinancialReports = () => isFinancial() || isFederation();
  const canViewMatchReports = () => isFederation();
  const canManageTickets = () => isFederation() || isAdmin();

  const can = (ability) => {
    switch (ability) {
      case 'admin.users':
        return canManageUsers();
      case 'admin.roles':
        return canManageRoles();
      case 'admin.payment':
        return canManagePaymentSettings();
      case 'club.list':
        return canListClubs();
      case 'club.review':
        return canReviewClubs();
      case 'match.manage':
        return canManageMatches();
      case 'base.read':
        return canReadBaseData();
      case 'base.manage':
        return canManageBaseData();
      case 'report.financial':
        return canViewFinancialReports();
      case 'report.match':
        return canViewMatchReports();
      case 'ticket.manage':
        return canManageTickets();
      default:
        return false;
    }
  };

  return {
    roleNames,
    isAdmin,
    isFederation,
    isFinancial,
    isProvinceBoard,
    provinceBoardId,
    canListClubs,
    canReviewClubs,
    canManageUsers,
    canManageRoles,
    canManagePaymentSettings,
    canManageMatches,
    canReadBaseData,
    canManageBaseData,
    canViewFinancialReports,
    canViewMatchReports,
    canManageTickets,
    can,
  };
};

export default useAccess;
